import { useNavigate } from "react-router-dom";
import { FaTimes, FaTicketAlt, FaGift, FaUsers, FaUser, FaCalendar, FaCheck, FaTimes as FaTimesRed, FaClock, FaChevronDown, FaChevronUp, FaPlus, FaUserPlus } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import { useEffect, useState } from "react";
import { commonModalSetting } from "JS/common";
import { adminIssueAllCoupons, cancelIssueAllCoupons, getCouponDetail } from "JS/API";
import SearchUserModal from "./SearchUserModal";

// 디자인 시스템 임포트
import { designTokens, commonStyles, badgeStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";

const CouponDetailModal = ({ setIsDetailModal, detailData }) => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  const [couponDetail, setCouponDetail] = useState(null);
  const [isOpenSearchModal, setIsOpenSearchModal] = useState(false);

  const [showAllIssued, setShowAllIssued] = useState(false);
  const issuedMembers = couponDetail?.issued_member || [];
  const isLongIssuedList = issuedMembers.length > 10;
  const displayedIssuedMembers =
    isLongIssuedList && !showAllIssued ? issuedMembers.slice(0, 10) : issuedMembers;
  const [showAllUsed, setShowAllUsed] = useState(false);
  const usedMembers = couponDetail?.used_member || [];
  const isLongUsedList = usedMembers.length > 10;
  const displayedUsedMembers =
    isLongUsedList && !showAllUsed ? usedMembers.slice(0, 10) : usedMembers;

  // 스타일 정의
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999,
      padding: designTokens.spacing.xxl
    },
    container: detailModalStyles.container,
    header: detailModalStyles.header,
    title: detailModalStyles.title,
    closeButton: detailModalStyles.closeButton,
    content: detailModalStyles.content,
    section: {
      marginBottom: designTokens.spacing.xxl
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      fontSize: '16px',
      fontWeight: '600',
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.lg,
      padding: `${designTokens.spacing.md} 0`,
      borderBottom: `2px solid ${designTokens.colors.primary}`
    },
    infoTable: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: designTokens.colors.surface,
      borderRadius: designTokens.borderRadius.lg,
      overflow: 'hidden',
      border: `1px solid ${designTokens.colors.border}`
    },
    tableRow: {
      borderBottom: `1px solid ${designTokens.colors.borderLight}`
    },
    tableHeaderCell: {
      padding: `${designTokens.spacing.lg} ${designTokens.spacing.xl}`,
      backgroundColor: designTokens.colors.background,
      fontWeight: '600',
      color: designTokens.colors.text.primary,
      fontSize: '14px',
      width: '160px',
      borderRight: `1px solid ${designTokens.colors.borderLight}`
    },
    tableCell: {
      padding: `${designTokens.spacing.lg} ${designTokens.spacing.xl}`,
      color: designTokens.colors.text.secondary,
      fontSize: '14px',
      lineHeight: '1.5'
    },
    statusBadge: {
      ...badgeStyles.base,
      marginLeft: designTokens.spacing.sm
    },
    memberList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: designTokens.spacing.sm,
      marginTop: designTokens.spacing.sm
    },
    memberTag: {
      ...badgeStyles.base,
      ...badgeStyles.neutral,
      fontSize: '12px'
    },
    moreInfo: {
      display: 'block',
      marginTop: designTokens.spacing.sm,
      fontWeight: '600',
      color: designTokens.colors.text.tertiary
    },
    toggleButton: {
      ...commonStyles.buttonBase,
      backgroundColor: 'transparent',
      color: designTokens.colors.primary,
      border: `1px solid ${designTokens.colors.primary}`,
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
      fontSize: '12px',
      marginTop: designTokens.spacing.sm
    },
    actionButtons: {
      display: 'flex',
      gap: designTokens.spacing.md,
      padding: designTokens.spacing.xxl,
      borderTop: `1px solid ${designTokens.colors.border}`,
      backgroundColor: designTokens.colors.background
    },
    primaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: '600'
    },
    secondaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.surface,
      color: designTokens.colors.primary,
      border: `1px solid ${designTokens.colors.primary}`,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: '600'
    }
  };

  // 상태별 배지 스타일
  const getStatusBadge = (status) => {
    const statusConfig = {
      '유효': { color: designTokens.colors.success, icon: <FaCheck size={10} />, bg: '#dcfce7' },
      '만료': { color: designTokens.colors.danger, icon: <FaTimesRed size={10} />, bg: '#fee2e2' },
      '대기': { color: designTokens.colors.status.pending, icon: <FaClock size={10} />, bg: '#fef3c7' }
    };
    
    const config = statusConfig[status] || statusConfig['대기'];
    
    return (
      <span style={{
        ...modalStyles.statusBadge,
        color: config.color,
        backgroundColor: config.bg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {config.icon}
        {status}
      </span>
    );
  };

  // 쿠폰 타입별 아이콘
  const getCouponTypeIcon = (type) => {
    return type === "프로모션" ? <FaGift /> : <FaTicketAlt />;
  };

  useEffect(() => {
    fetchDetail();
  }, [detailData]);

  const fetchDetail = async () => {
    const result = await getCouponDetail(detailData.id, detailData.coupon_code);
    if (typeof result === "object" && result?.data) {
      setCouponDetail(result.data);
    } else {
      setAlert("fetchFail");
      commonModalSetting(setAlertBox, true, "alert", "상세 정보를 불러오지 못했습니다.");
    }
  };

  const handleAllIssue = async () => {
    try {
      const payload = {
        id: couponDetail?.id,
        coupon_code: couponDetail?.coupon_code,
      };

      let result;
      if (couponDetail?.all_issued_yn?.includes("ALL")) {
        result = await cancelIssueAllCoupons(payload);
        if (result?.status === 200) {
          commonModalSetting(setAlertBox, true, "alert", "전체 발급이 취소되었습니다.");
        }
      } else {
        result = await adminIssueAllCoupons(payload);
        if (result?.status === 200) {
          commonModalSetting(setAlertBox, true, "alert", "전체 발급이 완료되었습니다.");
        }
      }
      await fetchDetail();
    } catch (err) {
      console.error(err);
      commonModalSetting(setAlertBox, true, "alert", "처리에 실패했습니다.");
    }
  };

  return (
    <>
      <div style={modalStyles.overlay}>
        <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={modalStyles.header}>
            <h3 style={modalStyles.title}>
              <FaTicketAlt />
              쿠폰 상세 정보
            </h3>
            <button
              style={modalStyles.closeButton}
              onClick={() => setIsDetailModal(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
                e.target.style.color = designTokens.colors.text.primary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = designTokens.colors.text.tertiary;
              }}
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* 컨텐츠 */}
          <div style={modalStyles.content}>
            {/* 쿠폰 기본정보 */}
            <div style={modalStyles.section}>
              <h4 style={modalStyles.sectionTitle}>
                {getCouponTypeIcon(couponDetail?.coupon_type)}
                쿠폰 기본정보
              </h4>
              <table style={modalStyles.infoTable}>
                <tbody>
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>쿠폰 유형</td>
                    <td style={modalStyles.tableCell}>
                      <span style={{
                        ...badgeStyles.base,
                        ...badgeStyles.primary,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {getCouponTypeIcon(couponDetail?.coupon_type)}
                        {couponDetail?.coupon_type}
                      </span>
                    </td>
                  </tr>
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>쿠폰 금액</td>
                    <td style={modalStyles.tableCell}>
                      <span style={{ fontWeight: '600', fontSize: '16px', color: designTokens.colors.primary }}>
                        {couponDetail?.discount_value}
                      </span>
                    </td>
                  </tr>
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>쿠폰 기간</td>
                    <td style={modalStyles.tableCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: designTokens.spacing.sm }}>
                        <FaCalendar size={12} style={{ color: designTokens.colors.text.tertiary }} />
                        {couponDetail?.period}
                        {getStatusBadge(couponDetail?.status)}
                      </div>
                    </td>
                  </tr>
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>쿠폰명</td>
                    <td style={modalStyles.tableCell}>
                      <span style={{ fontWeight: '600' }}>{couponDetail?.name}</span>
                    </td>
                  </tr>
                  {couponDetail?.coupon_type === "프로모션" && (
                    <tr style={modalStyles.tableRow}>
                      <td style={modalStyles.tableHeaderCell}>쿠폰 코드</td>
                      <td style={modalStyles.tableCell}>
                        <code style={{
                          backgroundColor: designTokens.colors.background,
                          padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
                          borderRadius: designTokens.borderRadius.sm,
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          border: `1px solid ${designTokens.colors.border}`
                        }}>
                          {couponDetail?.coupon_code}
                        </code>
                      </td>
                    </tr>
                  )}
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>쿠폰 설명</td>
                    <td style={modalStyles.tableCell}>{couponDetail?.description}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 쿠폰 사용 정보 */}
            <div style={modalStyles.section}>
              <h4 style={modalStyles.sectionTitle}>
                <FaUsers />
                쿠폰 사용 정보
              </h4>
              <table style={modalStyles.infoTable}>
                <tbody>
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: designTokens.spacing.xs }}>
                        <FaUser size={12} />
                        발급 대상자 ({issuedMembers.length})
                      </div>
                    </td>
                    <td style={modalStyles.tableCell}>
                      {issuedMembers.length === 0 ? (
                        <span style={{ color: designTokens.colors.text.muted }}>-</span>
                      ) : (
                        <>
                          <div style={modalStyles.memberList}>
                            {displayedIssuedMembers.map((member, index) => (
                              <span key={index} style={modalStyles.memberTag}>
                                {member}
                              </span>
                            ))}
                          </div>
                          {!showAllIssued && isLongIssuedList && (
                            <span style={modalStyles.moreInfo}>
                              + {issuedMembers.length - 10}명 더
                            </span>
                          )}
                          {isLongIssuedList && (
                            <button
                              style={modalStyles.toggleButton}
                              onClick={() => setShowAllIssued((prev) => !prev)}
                            >
                              {showAllIssued ? (
                                <>접기 <FaChevronUp size={10} /></>
                              ) : (
                                <>전체보기 <FaChevronDown size={10} /></>
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr style={modalStyles.tableRow}>
                    <td style={modalStyles.tableHeaderCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: designTokens.spacing.xs }}>
                        <FaCheck size={12} />
                        사용자 ({usedMembers.length})
                      </div>
                    </td>
                    <td style={modalStyles.tableCell}>
                      {usedMembers.length === 0 ? (
                        <span style={{ color: designTokens.colors.text.muted }}>-</span>
                      ) : (
                        <>
                          <div style={modalStyles.memberList}>
                            {displayedUsedMembers.map((member, index) => (
                              <span key={index} style={{
                                ...modalStyles.memberTag,
                                ...badgeStyles.success
                              }}>
                                {member}
                              </span>
                            ))}
                          </div>
                          {!showAllUsed && isLongUsedList && (
                            <span style={modalStyles.moreInfo}>
                              + {usedMembers.length - 10}명 더
                            </span>
                          )}
                          {isLongUsedList && (
                            <button
                              style={modalStyles.toggleButton}
                              onClick={() => setShowAllUsed((prev) => !prev)}
                            >
                              {showAllUsed ? (
                                <>접기 <FaChevronUp size={10} /></>
                              ) : (
                                <>전체보기 <FaChevronDown size={10} /></>
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {couponDetail?.coupon_type !== "프로모션" && (
                    <tr style={modalStyles.tableRow}>
                      <td style={modalStyles.tableHeaderCell}>전체 발급 여부</td>
                      <td style={modalStyles.tableCell}>
                        {(() => {
                          const status = couponDetail?.all_issued_yn;
                          if (status === "ALL") return (
                            <span style={{ ...badgeStyles.base, ...badgeStyles.success }}>전체 발급</span>
                          );
                          if (status === "PORTION") return (
                            <span style={{ ...badgeStyles.base, ...badgeStyles.warning }}>선택 발급</span>
                          );
                          if (status?.includes("ALL") && status?.includes("PORTION")) return (
                            <span style={{ ...badgeStyles.base, ...badgeStyles.primary }}>전체 + 선택 발급</span>
                          );
                          return <span style={{ color: designTokens.colors.text.muted }}>-</span>;
                        })()}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 액션 버튼 */}
          {couponDetail?.coupon_type !== "프로모션" && (
            <div style={modalStyles.actionButtons}>
              <button
                style={modalStyles.primaryButton}
                onClick={handleAllIssue}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = designTokens.colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = designTokens.colors.primary;
                }}
              >
                {couponDetail?.all_issued_yn?.includes("ALL") ? (
                  <>
                    <FaTimes size={12} />
                    전체 발급 취소
                  </>
                ) : (
                  <>
                    <FaPlus size={12} />
                    전체 발급
                  </>
                )}
              </button>
              <button
                style={modalStyles.secondaryButton}
                onClick={() => setIsOpenSearchModal(true)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = designTokens.colors.primary;
                  e.target.style.color = designTokens.colors.surface;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = designTokens.colors.surface;
                  e.target.style.color = designTokens.colors.primary;
                }}
              >
                <FaUserPlus size={12} />
                선택 발급
              </button>
            </div>
          )}
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
          }}
        />
      )}

      {isOpenSearchModal && (
        <SearchUserModal
          setIsOpenSearchModal={setIsOpenSearchModal}
          detailData={detailData}
          fetchDetail={fetchDetail}
        />
      )}
    </>
  );
};

export default CouponDetailModal;